import { Edit } from "@mui/icons-material";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
} from "@mui/material";
import { getSubscription, meetsVisibilityConditions } from "@utils";
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { DemoChip } from "src/components/DemoChip/DemoChip";
import { DemoCompleteModal } from "src/components/DemoCompleteModal/DemoCompleteModal";
import { EditDeviceModalContent } from "src/components/DeviceCreation/modal-list-items/EditDeviceModalContent";
import { PageBreadcrumb } from "src/components/PageBreadcrumb";
import { Cards } from "src/components/Roadmap/Cards/Cards";
import { areAllCardTasksCompleted } from "src/components/Roadmap/utils";
import { CompactSelect } from "src/components/ui/CompactSelect";
import { ROUTE_SLUGS, ROUTES } from "src/config/navigation/routes";
import { fillRouteWithSlugs } from "src/utils/navigation";
import { getOrgFromUser } from "src/utils/user";
import { PageHeader, RoadmapSkeleton, TaskModal } from "../components";
import { RoadmapErrorBoundary } from "../components/ErrorBoundary";
import {
  useGetDevice,
  useGetDevices,
  useGetDocuments,
  useGetDocumentsTemplates,
  useGetRoadmapCards,
  useGetRoadmapStages,
  useGetTasks,
  useGetTaskStates,
  useGetUser,
  useGlobalDeviceContext,
} from "../hooks";
import { useStore } from "../stores";
import { Document, RoadmapTask, User } from "../stores/models";

export const Roadmap = () => {
  const { deviceStore } = useStore();
  const { deviceId = "", orgId = "" } = useParams();
  const navigate = useNavigate();
  const { activeGlobalDeviceId, setActiveGlobalDeviceId } =
    useGlobalDeviceContext();
  if (!deviceId) throw new Error("No device ID provided");
  const { data: devices } = useGetDevices({
    orgId,
  });
  const [selectedCard, setSelectedCard] = useState<
    { cardKey: string; stageKey: string } | undefined
  >();
  const [editDevice, setEditDevice] = useState(false);
  let [_, setSearchParams] = useSearchParams();

  const { data: user } = useGetUser();
  const { data: device } = useGetDevice({ orgId, deviceId });
  const org = getOrgFromUser(user, orgId);
  const subscription = org ? getSubscription(org) : null;
  const { isLoading: tasksLoading, data: tasks } = useGetTasks(orgId, deviceId);
  const { data: cards } = useGetRoadmapCards({ orgId, deviceId });
  const { data: templatesConfig } = useGetDocumentsTemplates({
    orgId,
    deviceId,
  });
  const { data: documents } = useGetDocuments(orgId, deviceId);
  const { data: stages } = useGetRoadmapStages({ orgId, deviceId });
  const { data: taskStates } = useGetTaskStates({
    orgId,
    deviceId,
    enabled: !!orgId && !!deviceId,
  });

  useEffect(() => {
    if (!orgId || !deviceId || !activeGlobalDeviceId) {
      return;
    }

    if (activeGlobalDeviceId === deviceId) {
      return;
    }

    navigate(
      fillRouteWithSlugs(ROUTES.DEVICE_OVERVIEW, {
        [ROUTE_SLUGS.ORG_ID]: orgId,
        [ROUTE_SLUGS.DEVICE_ID]: activeGlobalDeviceId,
      })
    );
  }, [activeGlobalDeviceId, deviceId, navigate, orgId]);

  const checkCardCompletion = useMemo(
    () => (cardTasks: RoadmapTask[], documents: Document[], user: User) => {
      const cardDocuments = documents.filter(
        (document) =>
          document.templateKey !== null &&
          cardTasks.map((t) => t.taskKey).includes(document.templateKey)
      );

      return areAllCardTasksCompleted(
        cardTasks,
        cardDocuments,
        user,
        documents,
        orgId,
        taskStates
      );
    },
    [documents, user, orgId, taskStates]
  );

  if (
    !device ||
    tasksLoading ||
    !tasks ||
    !documents ||
    !user ||
    !org ||
    !subscription ||
    !devices ||
    !templatesConfig ||
    !stages
  ) {
    return <RoadmapSkeleton />;
  }

  const roadmap = deviceStore.mapRoadmap(
    tasks,
    device,
    documents,
    cards,
    stages
  );

  const handleClickOpenCardDialog = (cardKey: string, stageKey: string) => {
    setSelectedCard({ cardKey, stageKey });
  };

  const handleCloseCardDialog = () => {
    setSearchParams((params) => {
      params.delete("task");
      return params;
    });
    setSelectedCard(undefined);
  };

  const card = roadmap
    .find((step) => step.id === selectedCard?.stageKey)
    ?.cards.find((card) => card.cardKey === selectedCard?.cardKey);

  const first =
    "polygon(0 0,calc(100% - 6px) 0,100% 50%,calc(100% - 6px) 100%,0 100%)";
  const all =
    "polygon(calc(100% - 6px) 0,100% 50%,calc(100% - 6px) 100%,2px 100%,8px 50%,2px 0)";

  return (
    <RoadmapErrorBoundary
      device={device}
      tasks={tasks}
      documents={documents}
      user={user}
      subscription={subscription}
      devices={devices}
      templatesConfig={templatesConfig}
      stages={stages}
      roadmap={roadmap}
      selectedCard={selectedCard}
      setSelectedCard={setSelectedCard}
      setSearchParams={setSearchParams}
      editDevice={editDevice}
      setEditDevice={setEditDevice}
      card={card}
    >
      <div className="flex flex-col gap-y-3">
        <PageHeader
          pageTitleComponent={
            <div className="flex items-center gap-x-2">
              <PageBreadcrumb
                items={[
                  {
                    label:
                      devices.length > 0 ? (
                        <CompactSelect
                          textSize="medium"
                          value={deviceId}
                          onChange={(nextDeviceId) => {
                            setActiveGlobalDeviceId(nextDeviceId);
                            navigate(
                              fillRouteWithSlugs(ROUTES.DEVICE_OVERVIEW, {
                                [ROUTE_SLUGS.ORG_ID]: orgId,
                                [ROUTE_SLUGS.DEVICE_ID]: nextDeviceId,
                              })
                            );
                          }}
                          ariaLabel="Roadmap device"
                          renderValue={() => device.name}
                        >
                          {devices.map((deviceOption) => (
                            <MenuItem
                              key={deviceOption.id}
                              value={deviceOption.id}
                            >
                              {deviceOption.name}
                            </MenuItem>
                          ))}
                        </CompactSelect>
                      ) : (
                        device.name
                      ),
                  },
                  { label: "Roadmap" },
                ]}
              />
              {!!device.isDemoDevice && <DemoChip size="tiny" />}
              <IconButton
                size="small"
                aria-label="Edit device"
                onClick={() => setEditDevice(true)}
              >
                <Edit className="text-gray-400 size-[16px]" />
              </IconButton>
            </div>
          }
        />
        {!!device.isDemoDevice && <DemoCompleteModal orgId={orgId} />}

        <div className="flex flex-col py-2 px-3">
          <div className="flex">
            {roadmap.map((step, index) => {
              const stagesCardsStatus = step.cards.map((card) => {
                const cardTasks = tasks.filter(
                  (task) =>
                    task.parentCardKey === card.cardKey &&
                    meetsVisibilityConditions(
                      task.visibilityConditions,
                      device,
                      documents
                    )
                );

                return checkCardCompletion(cardTasks, documents, user);
              });

              const areAllStagesDocumentsCompleted = stagesCardsStatus.every(
                (status) => status === true
              );

              const percentageEach = 100 / stagesCardsStatus.length;
              const percentageCompleted = parseFloat(
                stagesCardsStatus
                  .reduce(
                    (acc, status) => (status ? acc : acc - percentageEach),
                    100
                  )
                  .toFixed(2)
              );

              const color1 = "rgb(21,128, 61)";
              const color2 = "rgb(203, 213, 225)";

              return (
                <div key={step.title} className="flex flex-1 flex-col">
                  <div
                    className={`flex flex-1 items-center justify-center px-4 py-1 `}
                    style={{
                      background: `linear-gradient(to right,  ${color1}, ${color1} ${percentageCompleted}%, ${color2} ${percentageCompleted}%, ${color2} ${100 - percentageCompleted}%)`,
                      color: areAllStagesDocumentsCompleted ? "white" : "black",
                      clipPath: index === 0 ? first : all,
                      display: "flex",
                      justifyContent: "center",
                      textAlign: "center",
                    }}
                  >
                    {step.title}
                  </div>
                </div>
              );
            })}
          </div>
          <Cards
            roadmap={roadmap}
            onClickOpenCardDialog={handleClickOpenCardDialog}
            documents={documents}
            user={user}
            device={device}
            templatesConfig={templatesConfig}
            tasks={tasks}
          />
        </div>
        <Dialog
          open={editDevice}
          onClose={() => setEditDevice(false)}
          maxWidth={"sm"}
        >
          <DialogTitle>Edit Device</DialogTitle>
          <DialogContent>
            <EditDeviceModalContent
              subscription={subscription}
              org={org}
              device={device}
              setEditDevice={setEditDevice}
              devices={devices}
            />
          </DialogContent>
        </Dialog>
        {!!selectedCard && !!card && (
          <TaskModal
            cardConfig={{ card, stageKey: selectedCard.stageKey }}
            open={!!selectedCard}
            handleCloseCardDialog={handleCloseCardDialog}
          />
        )}
      </div>
    </RoadmapErrorBoundary>
  );
};
